<script setup>
import { ref, computed } from "vue";
import { useFieldsStore } from "@/stores/storeFieldData";
import { useRequestsStore } from "@/stores/storeRequests";

const storeField = useFieldsStore();
const storeRequests = useRequestsStore();

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_FILES_COUNT = 10;
const responseError = ref(null);
const filesCount = computed(() => storeField.fields.files.length);
const isMaxFilesReached = computed(() => filesCount.value >= MAX_FILES_COUNT);

const handleFiles = (e) => {
	const files = Array.from(e.target.files);
	let fileId = storeField.fields.files.length > 0 ? Math.max(...storeField.fields.files.map((f) => f.id)) : 0;

	const allowedFormats = [".jpg", ".jpeg", ".png", ".webp", ".tiff", ".pdf", ".gif"];

	// Фильтруем только те файлы, которые можно добавить (не превышая лимит)
	const filesToAdd = files.slice(0, MAX_FILES_COUNT - filesCount.value);

	if (filesToAdd.length === 0) {
		responseError.value = `Максимальное количество файлов (${MAX_FILES_COUNT}) уже достигнуто.`;
		return;
	}

	for (let file of filesToAdd) {

		const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
        
        if (!allowedFormats.includes(fileExtension)) {
            responseError.value = `Формат файла "${fileExtension}" не поддерживается.`;
            continue;
        }

		if (file.size <= MAX_FILE_SIZE) {
			fileId++;
			storeField.fields.files.push({
				id: fileId,
				nameFile: file.name,
				sizeFile: file.size,
				imgFile: window.URL.createObjectURL(file),
			});
			storeField.fields.filesForPost.push(file);
			responseError.value = null
		} else {
			responseError.value = `Файл "${file.name}" превышает 5MB и не был добавлен.`;
		}
	}

	// Если попытались добавить больше файлов чем доступно
	if (files.length > filesToAdd.length) {
		responseError.value = `Добавлено ${filesToAdd.length} из ${files.length} файлов. Максимум ${MAX_FILES_COUNT} файлов.`;
	}
	e.target.value = '';
};

const deleteFile = (name) => {
	storeField.fields.files = storeField.fields.files.filter((file) => file.nameFile !== name);
	storeField.fields.filesForPost = storeField.fields.filesForPost.filter((file) => file.name !== name);
};

</script>
<template>
	<div class="file-holder">

		<label :class="['file', { '--disabled': isMaxFilesReached }]">
			<input type="file" class="file__real" accept=".jpg, .jpeg, .png, .gif, .tiff, .webp, .pdf" multiple @change="handleFiles" :disabled="isMaxFilesReached" />
			<span class="file__custom"></span>
			<span class="file__text" v-if="!isMaxFilesReached">Прикрепить файл</span>
			<span class="file__text" v-else>Максимум файлов достигнут</span>
		</label>
	
		<ul class="file-desc__list">
			<li class="file-desc__item">Разрешенные форматы файлов: .jpg, .jpeg, .png, .gif, .tiff, .webp, .pdf.</li>
			<li class="file-desc__item">Максимальный размер файла: 5 MB.</li>
			<li class="file-desc__item">Максимальное количество файлов: 10 (добавлено: {{ filesCount }}/10).</li>
		</ul>
		<p class="file-error" v-if="!!responseError">{{ responseError }}</p>	
		<div class="preview-file">
			<TransitionGroup name="list" tag="ul" class="preview-file__list">
				<li class="preview-file__item" v-for="{ file, id, imgFile, nameFile } in storeField.fields.files" :key="id">
					<img class="preview-file__file-image" width="40" height="40" :src="imgFile" alt="изображение" />
					<button class="preview-file__btn" type="button" @click="deleteFile(nameFile)">
						<svg width="20" height="20"><use href="#x"></use></svg>
					</button>
				</li>
			</TransitionGroup>
		</div>					
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.file-holder {
	display: grid;
	gap: 10px;
}
.file {
	width: fit-content;
	display: flex;
	align-items: center;
	gap: 0.8rem;
	margin-left: 5px;
	cursor: pointer;
	@media (hover: hover) {
		&:hover {
			.file__text {
				text-decoration: underline;
			}
		}
	}
}
.file__real {
	width: 0;
	height: 0;
	opacity: 0;
	position: absolute;
	z-index: -1;
}
.file__custom {
	background-image: url("@/assets/img/icons/paperclip.svg");
	background-repeat: no-repeat;
	background-position: 0px 0px; /* x y*/
	background-size: 20px;
    width: 20px;
    height: 20px;
	@media (max-width: vars.$x480) {
		background-size: 20px; /* w h*/
		width: 20px;
		height: 20px;
	}
}

.file__text {
	font-weight: 400;
	font-size: 16px;
	line-height: 160%;
	color: var(--text-accent-2-100);
	@media (max-width: vars.$x480) {
	}
}
.file-desc__list {
	
}
.file-desc__item {
	font-weight: 400;
	font-size: 14px;
	line-height: 150%;
	color: var(--text-inverse-100);
	@media (max-width: vars.$x480) {
		
	}
}
.preview-file {
	// margin-top: 20px;
}
.preview-file__list {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
	gap: 0.8rem;
	@media (max-width: vars.$x480) {
		grid-template-columns: repeat(auto-fill, minmax(6rem, 1fr));
	}
}
.preview-file__item {
	position: relative;
	overflow: hidden;
	border: 2px solid rgba(197, 198, 202, 0.24);
	border-radius: 0.8rem;
	width: 100%;
	aspect-ratio: 1/1;
}
.file-error {
	display: inline-block;
	margin-top: 10px;
	color: red;
}
.preview-file__file-image {
	width: 100%;
	height: 100%;
	object-fit: cover;
}
.preview-file__btn {
	position: absolute;
	top: 3px;
	right: 3px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 2px solid var(--border-accent-1-1);
	border-radius: 0.2rem;
	width: 2rem;
	height: 2rem;
	background: #fff;
	svg {
		width: 100%;
		height: 100%;
		stroke-width: 2px;
		fill: transparent;
		stroke: var(--border-accent-1-1);
	}
}

.list-enter-active,
.list-leave-active {
	transition: all 0.5s ease;
}
.list-enter-from,
.list-leave-to {
	opacity: 0;
	transform: translateX(30px);
}
</style>
